import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  TableContainer,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  Pets as PetsIcon,
  Phone as PhoneIcon,
  Badge as CpfIcon,
  CalendarToday as DateIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { GenericTable } from "@/components/GenericTable/GenericTable";
import { TableColumn } from "@/components/GenericTable/types";
import { GetAdminQuestionnaireResponse } from "@/services/fhir/questionnaireresponse/GetAdminQuestionnaireResponse";
import { oids } from "@/configs/Settings";
import { useSessionContext } from "@/contexts/SessionContext";

/**
 * @description Interface para os dados de log de acesso
 */
interface AccessLog {
  id: string;
  petId: string;
  petName: string;
  visitorCpf: string;
  visitorName: string;
  visitorPhone?: string;
  accessDate: string;
  reason: "consulta" | "encontrarPet" | "outro";

  // Campos formatados para exibição na tabela
  pet?: React.ReactNode;
  cpf?: string;
  telefone?: string;
  data?: string;
  hora?: string;
  motivo?: string;
}

/**
 * @description Interface para a resposta do QuestionnaireResponse
 */
interface QuestionnaireResponseItem {
  linkId: string;
  text: string;
  answer: Array<{
    valueString?: string;
    valueDateTime?: string;
    valueBoolean?: boolean;
  }>;
}

/**
 * @description Componente para exibir os logs de acesso aos pets
 * @returns {JSX.Element} Componente de logs de acesso
 */
export default function AccessLogs() {
  const { user } = useSessionContext();
  const [searchTerm, setSearchTerm] = useState("");

  // Estado para armazenar os dados de logs de acesso
  const [accessLogs, setAccessLogs] = useState<AccessLog[]>([]);

  /**
   * @description Retorna o texto do motivo de acesso
   */
  const getReasonText = (log: AccessLog) => {
    switch (log.reason) {
      case "consulta":
        return "Consulta";
      case "encontrarPet":
        return "Pet encontrado";
      case "outro":
        return log.reason || "Outro";
      default:
        return log.reason || "Não informado";
    }
  };

  // Função para formatar a data e hora a partir do formato ISO
  const formatISODateTime = (isoDate: string) => {
    try {
      const date = new Date(isoDate);
      return {
        date: format(date, "dd/MM/yyyy", { locale: ptBR }),
        time: format(date, "HH:mm", { locale: ptBR }),
      };
    } catch (error) {
      return { date: "-", time: "-" };
    }
  };

  // Preparar os dados para o GenericTable
  const tableData: AccessLog[] = accessLogs
    .filter(
      (log) =>
        log.petName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.visitorCpf.includes(searchTerm) ||
        log.visitorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.petId.includes(searchTerm) ||
        (log.visitorPhone && log.visitorPhone.includes(searchTerm))
    )
    .map((log) => {
      const { date, time } = formatISODateTime(log.accessDate);

      return {
        ...log,
        // Formatação dos dados para exibição
        pet: (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PetsIcon fontSize="small" sx={{ mr: 1, color: "primary.main" }} />
            {log.petName}
          </Box>
        ),
        cpf: log.visitorCpf,
        telefone: log.visitorPhone || "-",
        data: date,
        hora: time,
        motivo: getReasonText(log),
      };
    });

  // Definição das colunas para o GenericTable
  const tableColumns: TableColumn[] = [
    { column: "Pet", rowName: "pet", align: "center" },
    { column: "ID do Pet", rowName: "petId", align: "center" },
    {
      column: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CpfIcon fontSize="small" sx={{ mr: 0.5 }} />
          CPF
        </Box>
      ) as any,
      rowName: "cpf",
      align: "center",
    },
    { column: "Visitante", rowName: "visitorName", align: "center" },
    {
      column: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PhoneIcon fontSize="small" sx={{ mr: 0.5 }} />
          Telefone
        </Box>
      ) as any,
      rowName: "telefone",
      align: "center",
    },
    {
      column: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DateIcon fontSize="small" sx={{ mr: 0.5 }} />
          Data
        </Box>
      ) as any,
      rowName: "data",
      align: "center",
    },
    {
      column: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <TimeIcon fontSize="small" sx={{ mr: 0.5 }} />
          Hora
        </Box>
      ) as any,
      rowName: "hora",
      align: "center",
    },
    { column: "Motivo", rowName: "motivo", align: "center" },
  ];

  // Estilos personalizados para a tabela
  const tableStyles = {
    container: {
      width: "100%",
    },
    header: {
      backgroundColor: "background.default",
      fontWeight: "bold",
    },
    row: {
      "&:hover": {
        backgroundColor: "action.hover",
      },
    },
    cell: {
      textAlign: "center",
    },
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Função para buscar os dados de QuestionnaireResponse
  useEffect(() => {
    async function fetchAccessLogs() {
      try {
        setLoading(true);
        setError(null);

        // Busca os QuestionnaireResponses relacionados ao formulário de acesso aos pets
        const response = await GetAdminQuestionnaireResponse({
          questionnaire: "Questionnaire/petAccess",
          source: `Patient/${oids.cpf}-${user?.username}`,
          sort: "-_lastUpdated",
          // Não filtramos por author para pegar todos os registros
        });

        if (response && response.entry && response.entry.length > 0) {
          // Mapeia os dados do QuestionnaireResponse para o formato AccessLog
          const logs = response.entry
            .map((entry: any) => {
              const resource = entry.resource;
              if (
                !resource ||
                resource.resourceType !== "QuestionnaireResponse"
              ) {
                return null;
              }

              // Extrai os dados dos itens do QuestionnaireResponse
              const items: QuestionnaireResponseItem[] = resource.item || [];

              // Função auxiliar para encontrar o valor de um item específico
              const findItemValue = (linkId: string): string => {
                const item = items.find((item) => item.linkId === linkId);
                return item?.answer?.[0]?.valueString || "";
              };

              // Extrai os dados necessários
              const petId = findItemValue("idPet");
              const petName = findItemValue("nomePet");
              const visitorCpf = findItemValue("cpfAcesso");
              const visitorName = findItemValue("nomeAcesso");
              const visitorPhone = findItemValue("telefoneAcesso");
              const reason = findItemValue("motivoAcesso");

              // Cria o objeto AccessLog
              return {
                id: resource.id || "",
                petId,
                petName,
                visitorCpf,
                visitorName,
                visitorPhone: visitorPhone || undefined,
                accessDate: resource.authored || "",
                reason: reason || "Não informado",
              };
            })
            .filter(Boolean) as AccessLog[];

          setAccessLogs(logs);
        } else {
          setAccessLogs([]);
        }
      } catch (err: any) {
        console.error("Erro ao buscar logs de acesso:", err);
        setError(
          "Não foi possível carregar os logs de acesso. Tente novamente mais tarde."
        );
      } finally {
        setLoading(false);
      }
    }

    fetchAccessLogs();
  }, []);

  return (
    <Box>
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3, display: "flex", flexDirection: "column" }}>
          <Typography variant="h5" component="h1" gutterBottom>
            Histórico de Acessos aos Pets
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Visualize todos os acessos realizados aos dados dos pets.
          </Typography>

          <TextField
            fullWidth
            variant="outlined"
            placeholder="Pesquisar por nome do pet, CPF, telefone, ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mt: 2, mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Exibir mensagem de erro se houver */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Exibir loader durante o carregamento */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 5,
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body1" color="text.secondary">
              Carregando logs de acesso...
            </Typography>
          </Box>
        ) : accessLogs.length === 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            Nenhum registro de acesso encontrado.
          </Alert>
        ) : (
          <TableContainer variant="outlined" component={Paper}>
            <GenericTable
              tableColumns={tableColumns}
              tableRows={tableData}
              styles={tableStyles}
              characterLimit={30}
            />
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
